// App.js
import React from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import { ProtectedRoute, AdminRoute } from "./components/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import ErrorBoundary from "./components/ErrorBoundary";

import "./App.css";
import "leaflet/dist/leaflet.css";
import "./Leafletfix.js";
import ScrollToTop from "./components/ScrollToTop";

// Public Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import Register from "./pages/Register";
import Faq from "./pages/Faq";
import About from "./pages/About";
import Contacts from "./pages/Contacts";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";

// Protected Pages
import Welcome from "./pages/protected/Welcome";
import AllAuctions from "./pages/protected/AllAuctions";
import MyAuctions from "./pages/protected/MyAuctions";
import Results from "./pages/protected/Results";
import MyBids from "./pages/protected/MyBids";
import CreateAuction from "./pages/protected/CreateAuction";
import EditAuction from "./pages/protected/EditAuction";
import ViewAuction from "./pages/protected/ViewAuction";
import MySettings from "./pages/protected/MySettings";

// Hidden Pages
import PasswordReset from "./pages/Reset";
import Verification from "./pages/protected/hidden/Verification";

// Admin Pages
import Dashboard from "./pages/protected/admin/dashboard";
import Management from "./pages/protected/admin/management";

import Navbar from "./components/Navigation/Navbar";
import Footer from "./components/Navigation/Footer";
import CookieBanner from "./components/CookieBanner.js";

function MainLayout() {
  return (
    <>
      <CookieBanner />
      <Navbar />
      <ScrollToTop />
      <Outlet /> {/* <-- renders the child route */}
      <Footer />
    </>
  );
}

// Define all routes using the new data router
const router = createBrowserRouter([
  {
    element: (
      <AuthProvider>
        <HelmetWrapper />
        <MainLayout />
      </AuthProvider>
    ),
    children: [
      // Public Routes
      { path: "/", element: <Landing /> },
      { path: "/ienakt", element: <Login /> },
      { path: "/aizmirsuparoli", element: <ForgotPassword /> },
      { path: "/registreties", element: <Register /> },
      { path: "/buj", element: <Faq /> },
      { path: "/parmums", element: <About /> },
      { path: "/kontakti", element: <Contacts /> },
      { path: "/privatums", element: <PrivacyPolicy /> },
      { path: "/noteikumi", element: <TermsOfUse /> },
      { path: "/reset", element: <PasswordReset /> },

      // Protected routes
      {
        element: <ProtectedRoute />,
        children: [
          { path: "/visas_izsoles", element: <AllAuctions /> },
          { path: "/izsoles", element: <MyAuctions /> },
          { path: "/likmes", element: <MyBids /> },
          { path: "/izveidot", element: <CreateAuction /> },
          { path: "/labot/:id", element: <EditAuction /> },
          { path: "/profils", element: <MySettings /> },
          { path: "/sveicinats", element: <Welcome /> },
          { path: "/rezultats/:id", element: <Results /> },
          { path: "/izsole/:id", element: <ViewAuction /> },
          { path: "/h/verifikacija", element: <Verification /> },
        ],
      },

      // Admin routes
      {
        element: <AdminRoute />,
        children: [
          { path: "/a/dashboard", element: <Dashboard /> },
          { path: "/a/management", element: <Management /> },
        ],
      },

      // Catch-all redirect
      { path: "*", element: <Navigate to="/ienakt" replace /> },
    ],
  },
]);

// Separate Helmet wrapper for SEO
function HelmetWrapper() {
  const { t } = useTranslation();
  const defaultImageUrl = "https://www.cenaudze.lv/images/default-og-image.jpg";
  const defaultCanonical = "https://www.cenaudze.lv/";

  return (
    <>
      <Helmet>
        <title>{t("seo.title.default")}</title>
        <meta name="description" content={t("seo.description.default")} />
        <meta name="keywords" content={t("seo.keywords.default")} />
        <link rel="canonical" href={defaultCanonical} />
        <meta name="format-detection" content="telephone=no" />

        {/* Open Graph Tags */}
        <meta property="og:title" content={t("seo.title.default")} />
        <meta
          property="og:description"
          content={t("seo.description.default")}
        />
        <meta property="og:image" content={defaultImageUrl} />
        <meta property="og:url" content={defaultCanonical} />
        <meta property="og:type" content="website" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("seo.title.default")} />
        <meta
          name="twitter:description"
          content={t("seo.description.default")}
        />
        <meta name="twitter:image" content={defaultImageUrl} />
      </Helmet>
    </>
  );
}

function AppWrapper() {
  const content = (
    <>
      <RouterProvider router={router} />
    </>
  );

  return process.env.NODE_ENV === "production" ? (
    <ErrorBoundary>{content}</ErrorBoundary>
  ) : (
    content
  );
}

export default AppWrapper;
